'use client';

import { useState, useEffect, useCallback } from 'react';
import { useReduxUser } from '@hooks/common/useAuthClient/useReduxUser';

const MC_CONTEXT_KEY = 'mc_context';
const MC_CONTEXT_EVENT = 'mc-context-change';

export type MissionControlContext = 'candidate' | 'company';

interface UseMissionControlContextReturn {
  currentContext: MissionControlContext;
  switchContext: () => void;
  clearContext: () => void;
}

/**
 * @description Hook for managing Mission Control context (candidate vs company view).
 *
 * - Auto-defaults to 'company' when the authenticated user has a business wallet
 *   and has not explicitly saved a preference in localStorage.
 * - Stores the user's explicit preference in localStorage once they switch context.
 * - Context persists across sessions until cleared (on logout).
 */
export const useMissionControlContext = (): UseMissionControlContextReturn => {
  const { headerInfo } = useReduxUser();
  const isCompany = !!(headerInfo as ApiSchema<'HeaderInfo'> | undefined)?.hasBusinessWallet;

  const [currentContext, setCurrentContext] = useState<MissionControlContext>('candidate');
  const [isInitialized, setIsInitialized] = useState(false);

  // On mount: use stored preference if present, otherwise derive from user type.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const stored = localStorage.getItem(MC_CONTEXT_KEY);
      if (stored === 'candidate' || stored === 'company') {
        setCurrentContext(stored);
      } else {
        // No stored preference — default company users to 'company' context.
        setCurrentContext(isCompany ? 'company' : 'candidate');
      }
    } catch (error) {
      console.warn('Failed to read mission control context from localStorage:', error);
      setCurrentContext(isCompany ? 'company' : 'candidate');
    }

    setIsInitialized(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If headerInfo loads after mount and there is still no stored preference,
  // switch to company context automatically.
  useEffect(() => {
    if (!isInitialized) return;
    if (!isCompany) return;

    try {
      const stored = localStorage.getItem(MC_CONTEXT_KEY);
      if (!stored) {
        setCurrentContext('company');
      }
    } catch {
      // ignore
    }
  }, [isCompany, isInitialized]);

  // Persist explicit changes to localStorage.
  useEffect(() => {
    if (typeof window === 'undefined' || !isInitialized) return;

    try {
      localStorage.setItem(MC_CONTEXT_KEY, currentContext);
    } catch (error) {
      console.warn('Failed to save mission control context to localStorage:', error);
    }
  }, [currentContext, isInitialized]);

  // Listen for context changes dispatched by other hook instances (e.g. header vs MissionControl).
  useEffect(() => {
    const handleExternalSwitch = (e: Event) => {
      const next = (e as CustomEvent<MissionControlContext>).detail;
      if (next === 'candidate' || next === 'company') {
        setCurrentContext(next);
      }
    };

    window.addEventListener(MC_CONTEXT_EVENT, handleExternalSwitch);
    return () => window.removeEventListener(MC_CONTEXT_EVENT, handleExternalSwitch);
  }, []);

  const switchContext = useCallback(() => {
    setCurrentContext((prev) => {
      const next: MissionControlContext = prev === 'candidate' ? 'company' : 'candidate';
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent<MissionControlContext>(MC_CONTEXT_EVENT, { detail: next }));
      }, 0);
      return next;
    });
  }, []);

  const clearContext = useCallback(() => {
    setCurrentContext('candidate');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent<MissionControlContext>(MC_CONTEXT_EVENT, { detail: 'candidate' }));
    }, 0);

    if (typeof window === 'undefined') return;

    try {
      localStorage.removeItem(MC_CONTEXT_KEY);
    } catch (error) {
      console.warn('Failed to clear mission control context from localStorage:', error);
    }
  }, []);

  return {
    currentContext,
    switchContext,
    clearContext,
  };
};
